import { useId, type ReactNode } from 'react';
import type { MarathonAccount } from '../../lib/api';
import { useSkin } from '../../hooks/useSkin';
import { INK, ICE, TEAL, RIM, PX_FONT, TITLE_FONT, GOLD, FIELD, WIN, LOSS } from '../guild/theme';

/* A completed cohort drawn as a quartered heraldic shield: four subs in the quarters, the
   master (or, after the restructure, the fifth sub) as the centre escutcheon. Evaluations
   are dashed, as in the playbook; the whole crest lights gold on its turn. */

const FUNDED = 'var(--pal-crest-funded)';
const EVAL = 'var(--pal-crest-eval)';

export interface EmblemProps {
  master?: MarathonAccount;
  subs: (MarathonAccount | undefined)[];
  on: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}

const fillOf = (a?: MarathonAccount) => (a?.status === 'funded' ? FUNDED : EVAL);
const dashOf = (a?: MarathonAccount) => (a?.status === 'eval' ? '5 4' : undefined);

function Tag({ a, x, y, big = false, small = false }: { a?: MarathonAccount; x: number; y: number; big?: boolean; small?: boolean }) {
  if (!a) return null;
  const pct = Math.max(0, Math.min(1, a.room / a.mdd));
  const w = small ? 28 : big ? 44 : 36;
  if (small) {
    return (
      <g pointerEvents="none">
        <text x={x} y={y} textAnchor="middle" fontFamily={TITLE_FONT} fontWeight={700} fontSize={11} fill={a.role === 'master' ? GOLD : ICE}>{a.label}</text>
        <rect x={x - w / 2} y={y + 4} width={w} height={3} fill={FIELD} />
        <rect x={x - w / 2} y={y + 4} width={w * pct} height={3} fill={pct > 0.5 ? WIN : pct > 0.25 ? GOLD : LOSS} />
        <text x={x} y={y + 15} textAnchor="middle" fontFamily={PX_FONT} fontSize={5.5} letterSpacing="0.12em" fill={TEAL}>{a.status === 'eval' ? 'EVAL' : 'FUNDED'}</text>
      </g>
    );
  }
  return (
    <g pointerEvents="none">
      <text x={x} y={y} textAnchor="middle" fontFamily={TITLE_FONT} fontSize={big ? 17 : 15} fill={a.role === 'master' ? GOLD : ICE}>{a.label}</text>
      <rect x={x - w / 2} y={y + 6} width={w} height={4} fill={FIELD} />
      <rect x={x - w / 2} y={y + 6} width={w * pct} height={4} fill={pct > 0.5 ? WIN : pct > 0.25 ? GOLD : LOSS} />
      <text x={x} y={y + 22} textAnchor="middle" fontFamily={PX_FONT} fontSize={7} letterSpacing="0.1em" fill={TEAL}>
        {a.status === 'eval' ? 'EVAL' : 'FUNDED'}
      </text>
    </g>
  );
}

function Frame({ on, children, caption, width }: { on: boolean; children: ReactNode; caption: string; width: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg viewBox="0 0 240 250" width={width} height={width * 250 / 240} className={on ? 'pal-firing' : undefined} style={{ overflow: 'visible' }}>
        {children}
      </svg>
      <span style={{ fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: on ? GOLD : TEAL }}>{caption}</span>
    </div>
  );
}

const hit = (a: MarathonAccount | undefined, onSelect?: (id: string) => void) =>
  a && onSelect ? { onClick: () => onSelect(a.id), style: { cursor: 'pointer' } } : {};

export function CrestEmblem({ master, subs, on, selectedId, onSelect, width = 240 }: EmblemProps & { width?: number }) {
  const clipId = `crest-${useId().replace(/:/g, '')}`;
  const shield = 'M20 16 H220 V122 C220 188 120 238 120 238 C120 238 20 188 20 122 Z';
  const quads: [number, number, number, number][] = [[20, 16, 100, 106], [120, 16, 100, 106], [20, 122, 100, 120], [120, 122, 100, 120]];
  const stroke = on ? GOLD : RIM;
  const inner = 'M95 96 H145 V126 C145 146 120 160 120 160 C120 160 95 146 95 126 Z';
  return (
    <Frame on={on} width={width} caption={on ? 'On — all five take the trade' : 'Off — sits this one out'}>
      <defs><clipPath id={clipId}><path d={shield} /></clipPath></defs>
      <g clipPath={`url(#${clipId})`}>
        {quads.map(([x, y, w, h], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} fill={fillOf(subs[i])} {...hit(subs[i], onSelect)}
            stroke={subs[i]?.id === selectedId ? ICE : 'none'} strokeWidth={3} />
        ))}
      </g>
      <path d={shield} fill="none" stroke={INK} strokeWidth={7} />
      <path d={shield} fill="none" stroke={stroke} strokeWidth={3} />
      <line x1={120} y1={16} x2={120} y2={238} stroke={stroke} strokeWidth={3} />
      <line x1={20} y1={122} x2={220} y2={122} stroke={stroke} strokeWidth={3} />
      {subs.map((s, i) => s?.status === 'eval' && (
        <rect key={`d${i}`} x={quads[i][0] + 6} y={quads[i][1] + 6} width={quads[i][2] - 12} height={quads[i][3] - 12} fill="none" stroke={TEAL} strokeDasharray="5 4" clipPath={`url(#${clipId})`} pointerEvents="none" />
      ))}
      <path d={inner} fill={INK} transform="translate(0 2)" />
      <path d={inner} fill="var(--pal-node)" stroke={on ? GOLD : TEAL} strokeWidth={2.5} strokeDasharray={dashOf(master)} {...hit(master, onSelect)} />
      <Tag a={subs[0]} x={68} y={60} /><Tag a={subs[1]} x={172} y={60} />
      <Tag a={subs[2]} x={70} y={168} /><Tag a={subs[3]} x={170} y={168} />
      <Tag a={master} x={120} y={122} big />
    </Frame>
  );
}

/* The playbook's own picture of a cohort: a diamond split into four triangles (the subs) around a
   round centre (the master). Evaluations are dashed outlines; the whole diamond glows on its turn. */
export function DiamondEmblem({ master, subs, on, selectedId, onSelect, width: w = 240 }: EmblemProps & { width?: number }) {
  const C = { x: 120, y: 125 };
  // Each triangle is inset from the centre cross so the gaps are real space and the outer corners stay crisp
  const tris = ['14,121 116,19 116,121', '124,19 226,121 124,121', '14,129 116,231 116,129', '226,129 124,231 124,129'];
  const tags: [number, number][] = [[82, 86], [158, 86], [84, 158], [156, 158]];
  const lit = on ? 'var(--pal-accent)' : RIM;
  return (
    <Frame on={on} width={w * 1.7} caption={on ? 'On — all five take the trade' : 'Off — sits this one out'}>
      {tris.map((pts, i) => {
        const a = subs[i];
        const evalAcct = a?.status === 'eval';
        return (
          <polygon key={i} points={pts} {...hit(a, onSelect)}
            fill={!a ? 'none' : evalAcct ? 'var(--pal-crest-eval)' : on ? 'var(--pal-tint-accent)' : 'var(--pal-crest-funded)'}
            stroke={a?.id === selectedId ? ICE : evalAcct || !a ? TEAL : lit} strokeWidth={a?.id === selectedId ? 3 : 1.5}
            strokeDasharray={evalAcct || !a ? '5 4' : undefined} strokeLinejoin="miter" />
        );
      })}
      <circle cx={C.x} cy={C.y} r={28} fill="var(--pal-panel)" />
      <circle cx={C.x} cy={C.y} r={24} fill={master?.status === 'eval' ? 'var(--pal-crest-eval)' : 'var(--pal-node)'}
        stroke={master?.id === selectedId ? ICE : on ? 'var(--pal-accent)' : TEAL} strokeWidth={2.5} strokeDasharray={dashOf(master)} {...hit(master, onSelect)} />
      {subs.map((s, i) => <Tag key={i} a={s} x={tags[i][0]} y={tags[i][1]} small />)}
      <Tag a={master} x={C.x} y={C.y - 2} small />
    </Frame>
  );
}

/** The cohort emblem for the active skin: heraldic crest in the arcade, the playbook diamond elsewhere. */
export function CohortEmblem(props: EmblemProps & { width?: number }) {
  return useSkin().skin === 'arcade' ? <CrestEmblem {...props} /> : <DiamondEmblem {...props} />;
}
